/**
 * Custom React hook for WebSocket connection with automatic reconnection.
 *
 * Features:
 * - Automatic connection/reconnection with exponential backoff
 * - Fast reconnection for page refresh (500ms)
 * - Message parsing and dispatching
 * - Connection state management
 * - Type-safe message handling
 * - Cleanup on unmount
 * - Duplicate connection prevention
 */

import { useEffect, useRef, useState, useCallback } from 'react'
import {
  WSMessage,
  WSMessageType,
  ChatMessage,
  ConnectionStatus,
  MessageRole,
  ToolStatus,
  WorkflowStep,
  Artifact,
  ArtifactType,
  ManeuverArtifactData,
} from '../types/chat'
import { useErrorToasts } from './useToast'
import { fetchWithTimeout } from '../lib/http'

interface UseWebSocketOptions {
  sessionId: string
  url?: string
  token?: string | null
  reconnectInterval?: number
  maxReconnectAttempts?: number
}

interface UseWebSocketReturn {
  messages: ChatMessage[]
  connectionStatus: ConnectionStatus
  reconnectAttempts: number
  /** Returns true when the message reached the socket, false when dropped (and toasts the user). */
  sendMessage: (content: string, referenceTime?: string | null) => boolean
  disconnect: () => void
  reconnect: () => void
}

const DEFAULT_WS_URL = import.meta.env.VITE_WS_URL || 'ws://localhost:8000'
const DEFAULT_RECONNECT_INTERVAL = 1000 // Start at 1 second
const MAX_RECONNECT_ATTEMPTS = 5
const FIRST_RECONNECT_DELAY = 500 // Fast reconnect for page refresh (500ms)

// Backend API base URL for converting relative paths to full URLs
const API_BASE_URL = import.meta.env.VITE_API_URL || 'http://localhost:8000'

/**
 * Type guard to check if artifact data is ManeuverArtifactData
 */
function isManeuverArtifactData(data: unknown): data is ManeuverArtifactData {
  if (!data || typeof data !== 'object') return false
  const d = data as Record<string, unknown>
  return (
    typeof d.delta_v === 'object' &&
    d.delta_v !== null &&
    typeof d.maneuver_time === 'string' &&
    typeof d.predicted_miss_distance_km === 'number' &&
    typeof d.tca_shift_seconds === 'number' &&
    typeof d.primary_norad_id === 'number' &&
    typeof d.secondary_norad_id === 'number'
  )
}

/**
 * Convert relative plot paths to root-relative URLs.
 *
 * Plot paths like "plots/maneuvers/..." are served by the backend at /plots/.
 * In dev, Vite proxies /plots/ to the backend. In production, the reverse
 * proxy (Railway/nginx) routes /plots/ to the API server.
 *
 * This avoids hardcoding API_BASE_URL into iframe src attributes, which breaks
 * when the API domain is unreachable (e.g., api.astrowrangler.space not deployed).
 */
function toPlotUrl(relativePath: string): string {
  if (!relativePath) return ''
  // If already absolute, return as-is
  if (relativePath.startsWith('http://') || relativePath.startsWith('https://')) {
    return relativePath
  }
  // Ensure path starts with / so the browser resolves it against the current origin
  const cleanPath = relativePath.startsWith('/') ? relativePath : `/${relativePath}`
  return cleanPath
}

/**
 * Normalize maneuver artifact data - convert relative paths to absolute URLs
 */
function normalizeManeuverArtifact(artifact: Artifact): Artifact {
  if (artifact.type !== ArtifactType.MANEUVER || !artifact.data) {
    return artifact
  }

  // Check if data matches maneuver structure
  if (!isManeuverArtifactData(artifact.data)) {
    console.warn('[WebSocket] Maneuver artifact data does not match expected structure:', artifact.data)
    return artifact
  }

  const data = artifact.data as ManeuverArtifactData
  return {
    ...artifact,
    data: {
      ...data,
      // Per ADR-011, mission_control_path is a fully-qualified Supabase Storage
      // URL (e.g. https://...supabase.co/storage/v1/object/public/...). The
      // toPlotUrl helper passes http(s) URLs through unchanged, so this works
      // without special-casing.
      mission_control_path: toPlotUrl(data.mission_control_path || ''),
    },
  }
}

export function useWebSocket({
  sessionId,
  url = DEFAULT_WS_URL,
  token = null,
  reconnectInterval = DEFAULT_RECONNECT_INTERVAL,
  maxReconnectAttempts = MAX_RECONNECT_ATTEMPTS,
}: UseWebSocketOptions): UseWebSocketReturn {
  const [messages, setMessages] = useState<ChatMessage[]>([])
  const [connectionStatus, setConnectionStatus] = useState<ConnectionStatus>(
    ConnectionStatus.DISCONNECTED
  )
  const [reconnectAttempts, setReconnectAttempts] = useState(0)

  const wsRef = useRef<WebSocket | null>(null)
  const reconnectAttemptsRef = useRef(0)
  const reconnectTimeoutRef = useRef<ReturnType<typeof setTimeout> | null>(null)
  const shouldReconnectRef = useRef(true)
  const isIntentionalDisconnectRef = useRef(false)
  const wasConnectedRef = useRef(false)

  const errorToasts = useErrorToasts()

  /**
   * Handle incoming WebSocket messages
   */
  const handleWebSocketMessage = useCallback((wsMessage: WSMessage) => {
    console.log('[WebSocket] Received:', wsMessage.type, wsMessage)

    switch (wsMessage.type) {
      case WSMessageType.RESPONSE:
        // Add agent response message, or update existing placeholder with content
        setMessages((prev) => {
          const lastMessage = prev[prev.length - 1]

          // If last message is an agent message with empty content (placeholder from tool_start),
          // update it with the response content instead of creating a new message
          if (
            lastMessage?.role === MessageRole.AGENT &&
            lastMessage.content === '' &&
            lastMessage.workflow_steps.length > 0
          ) {
            return [
              ...prev.slice(0, -1),
              {
                ...lastMessage,
                id: wsMessage.message_id || lastMessage.id,
                content: wsMessage.content,
              },
            ]
          }

          // Otherwise create a new agent message
          return [
            ...prev,
            {
              id: wsMessage.message_id,
              session_id: sessionId,
              role: MessageRole.AGENT,
              content: wsMessage.content,
              workflow_steps: [],
              artifacts: [],
              created_at: new Date().toISOString(),
            },
          ]
        })
        break

      case WSMessageType.TOOL_START:
        // Update last message with new workflow step, or create placeholder agent message
        setMessages((prev) => {
          const lastMessage = prev[prev.length - 1]
          const newStep = {
            tool: wsMessage.tool,
            status: ToolStatus.RUNNING,
            started_at: new Date().toISOString(),
            completed_at: null,
            duration_ms: null,
            request: wsMessage.request,
            response: null,
            error: null,
          }

          // If last message is already an agent message, add step to it
          if (lastMessage?.role === MessageRole.AGENT) {
            return [
              ...prev.slice(0, -1),
              {
                ...lastMessage,
                workflow_steps: [...lastMessage.workflow_steps, newStep],
              },
            ]
          }

          // Otherwise, create a placeholder agent message for the workflow steps.
          // This handles the case where tool_start arrives before the response.
          // id stays null until RESPONSE arrives with the real UUID — this hides
          // the reaction bar (per AgentMessage's `message.id && ...` guard) so a
          // user can't dispatch feedback against a non-uuid placeholder id and
          // hit a 22P02 uuid validation error in /api/feedback.
          return [
            ...prev,
            {
              id: null,
              session_id: sessionId,
              role: MessageRole.AGENT,
              content: '', // Will be updated when response arrives
              workflow_steps: [newStep],
              artifacts: [],
              created_at: new Date().toISOString(),
            },
          ]
        })
        break

      case WSMessageType.TOOL_COMPLETE:
        // Update workflow step with completion data
        setMessages((prev) => {
          const lastMessage = prev[prev.length - 1]
          if (lastMessage?.role === MessageRole.AGENT) {
            const steps = [...lastMessage.workflow_steps]
            const stepIndex = steps.findIndex((s) => s.tool === wsMessage.tool && s.status === ToolStatus.RUNNING)
            if (stepIndex !== -1) {
              steps[stepIndex] = {
                ...steps[stepIndex],
                status: ToolStatus.COMPLETE,
                completed_at: new Date().toISOString(),
                duration_ms: wsMessage.duration_ms,
                response: wsMessage.response,
              }
            }
            return [
              ...prev.slice(0, -1),
              { ...lastMessage, workflow_steps: steps },
            ]
          }
          return prev
        })
        break

      case WSMessageType.TOOL_ERROR:
        // Update workflow step with error
        setMessages((prev) => {
          const lastMessage = prev[prev.length - 1]
          if (lastMessage?.role === MessageRole.AGENT) {
            const steps = [...lastMessage.workflow_steps]
            const stepIndex = steps.findIndex((s) => s.tool === wsMessage.tool && s.status === ToolStatus.RUNNING)
            if (stepIndex !== -1) {
              steps[stepIndex] = {
                ...steps[stepIndex],
                status: ToolStatus.ERROR,
                completed_at: new Date().toISOString(),
                error: wsMessage.error,
              }
            }
            return [
              ...prev.slice(0, -1),
              { ...lastMessage, workflow_steps: steps },
            ]
          }
          return prev
        })
        // Show toast notification for tool error
        errorToasts.toolExecutionError(wsMessage.tool)
        break

      case WSMessageType.ARTIFACT:
        // Add artifact to last agent message, normalizing maneuver artifacts
        setMessages((prev) => {
          const lastMessage = prev[prev.length - 1]
          if (lastMessage?.role === MessageRole.AGENT) {
            // Normalize maneuver artifacts to convert relative URLs to absolute
            const normalizedArtifact = normalizeManeuverArtifact(wsMessage.artifact)

            console.log('[WebSocket] Processing artifact:', {
              type: normalizedArtifact.type,
              title: normalizedArtifact.title,
              hasData: !!normalizedArtifact.data,
            })

            return [
              ...prev.slice(0, -1),
              {
                ...lastMessage,
                artifacts: [...lastMessage.artifacts, normalizedArtifact],
              },
            ]
          }
          return prev
        })
        break

      case WSMessageType.ERROR:
        console.error('[WebSocket] Server error:', wsMessage.message, wsMessage.details)
        // Check for specific error types
        if (wsMessage.message?.toLowerCase().includes('rate limit')) {
          errorToasts.spaceTrackRateLimit()
        } else {
          errorToasts.genericError(wsMessage.message)
        }
        break

      case WSMessageType.STATUS:
        console.log('[WebSocket] Status update:', wsMessage.status, wsMessage.details)
        break

      default:
        console.warn('[WebSocket] Unknown message type:', wsMessage)
    }
  }, [sessionId, errorToasts])

  /**
   * Connect to WebSocket server
   */
  const connect = useCallback(() => {
    // Close existing connection if any (prevent duplicates on page refresh)
    if (wsRef.current) {
      if (wsRef.current.readyState === WebSocket.OPEN) {
        console.log('[WebSocket] Closing existing connection before reconnect')
        wsRef.current.close()
      }
      wsRef.current = null
    }

    // Build WebSocket URL with optional auth token
    const baseUrl = `${url}/ws/chat/${sessionId}`
    const wsUrl = token ? `${baseUrl}?token=${encodeURIComponent(token)}` : baseUrl
    console.log('[WebSocket] Connecting to:', baseUrl) // Don't log token

    setConnectionStatus(
      reconnectAttemptsRef.current > 0
        ? ConnectionStatus.RECONNECTING
        : ConnectionStatus.CONNECTING
    )

    const ws = new WebSocket(wsUrl)

    ws.onopen = () => {
      console.log('[WebSocket] Connected')
      setConnectionStatus(ConnectionStatus.CONNECTED)

      // Show reconnection success toast if this was a reconnection
      if (wasConnectedRef.current && reconnectAttemptsRef.current > 0) {
        errorToasts.websocketReconnected()
      }

      wasConnectedRef.current = true
      reconnectAttemptsRef.current = 0 // Reset reconnect counter on success
      setReconnectAttempts(0) // Reset UI counter
    }

    ws.onmessage = (event) => {
      try {
        const wsMessage: WSMessage = JSON.parse(event.data)
        handleWebSocketMessage(wsMessage)
      } catch (error) {
        console.error('[WebSocket] Failed to parse message:', error)
      }
    }

    ws.onerror = (error) => {
      console.error('[WebSocket] Error:', error)
      setConnectionStatus(ConnectionStatus.ERROR)

      // Show error toast on first connection failure
      if (wasConnectedRef.current) {
        errorToasts.websocketError()
      }
    }

    ws.onclose = (event) => {
      console.log('[WebSocket] Disconnected', { code: event.code, reason: event.reason })
      setConnectionStatus(ConnectionStatus.DISCONNECTED)

      // Don't reconnect if this was intentional (e.g., React.StrictMode cleanup)
      if (isIntentionalDisconnectRef.current) {
        console.log('[WebSocket] Intentional disconnect - not reconnecting')
        isIntentionalDisconnectRef.current = false
        return
      }

      // Attempt reconnection if enabled and not exceeded max attempts
      if (
        shouldReconnectRef.current &&
        reconnectAttemptsRef.current < maxReconnectAttempts
      ) {
        reconnectAttemptsRef.current += 1
        setReconnectAttempts(reconnectAttemptsRef.current)

        // Use faster reconnect for first attempt (page refresh scenario)
        const delay = reconnectAttemptsRef.current === 1
          ? FIRST_RECONNECT_DELAY  // 500ms for first reconnect (fast page refresh recovery)
          : Math.min(
              reconnectInterval * Math.pow(2, reconnectAttemptsRef.current - 2),
              30000 // Cap at 30 seconds
            )

        console.log(
          `[WebSocket] Reconnecting in ${delay}ms (attempt ${reconnectAttemptsRef.current}/${maxReconnectAttempts})`
        )

        reconnectTimeoutRef.current = setTimeout(() => {
          connect()
        }, delay)
      } else if (reconnectAttemptsRef.current >= maxReconnectAttempts) {
        console.error('[WebSocket] Max reconnection attempts reached')
        errorToasts.websocketMaxReconnectFailed()
      }
    }

    wsRef.current = ws
  }, [sessionId, url, token, reconnectInterval, maxReconnectAttempts, handleWebSocketMessage, errorToasts])

  /**
   * Send chat message to server.
   *
   * Sole authoritative gate for "is the socket open?" — reads `wsRef.current.readyState`
   * directly instead of `connectionStatus` React state, which can lag a frame behind
   * the actual socket state. Callers must NOT pre-gate on `connectionStatus`; doing so
   * caused the silent PLAN MANEUVER drop in run-006 where UI showed ONLINE but the
   * handler closure had a stale CONNECTING value.
   */
  const sendMessage = useCallback(
    (content: string, referenceTime?: string | null): boolean => {
      if (wsRef.current?.readyState !== WebSocket.OPEN) {
        console.error('[WebSocket] Cannot send message: not connected')
        errorToasts.genericError('Not connected to server. Reconnecting…')
        return false
      }

      // Add user message to local state immediately
      const userMessage: ChatMessage = {
        id: null,
        session_id: sessionId,
        role: MessageRole.USER,
        content,
        workflow_steps: [],
        artifacts: [],
        created_at: new Date().toISOString(),
      }
      setMessages((prev) => [...prev, userMessage])

      // Send to server — include reference_time when sandbox mode is active
      const wsMessage: Record<string, unknown> = {
        type: WSMessageType.CHAT,
        content,
      }
      if (referenceTime) {
        wsMessage.reference_time = referenceTime
      }
      wsRef.current.send(JSON.stringify(wsMessage))
      console.log('[WebSocket] Sent message:', content, referenceTime ? `[sandbox: ${referenceTime}]` : '')
      return true
    },
    [sessionId, errorToasts]
  )

  /**
   * Manually disconnect WebSocket
   */
  const disconnect = useCallback(() => {
    shouldReconnectRef.current = false
    if (reconnectTimeoutRef.current) {
      clearTimeout(reconnectTimeoutRef.current)
      reconnectTimeoutRef.current = null
    }
    wsRef.current?.close()
    wsRef.current = null
  }, [])

  /**
   * Manually trigger reconnection
   */
  const reconnect = useCallback(() => {
    disconnect()
    shouldReconnectRef.current = true
    reconnectAttemptsRef.current = 0
    setReconnectAttempts(0)
    connect()
  }, [connect, disconnect])

  /**
   * Connect when session or token changes, cleanup on unmount.
   * Depends on both sessionId and token so switching sessions creates a new connection.
   */
  useEffect(() => {
    // In development without Supabase configured, connect without a token
    // (backend allows unauthenticated WebSocket when APP_ENV=development)
    const isDev = import.meta.env.DEV
    const hasToken = !!token

    if (!hasToken && !isDev) {
      console.log('[WebSocket] Waiting for auth token...')
      return
    }

    if (!hasToken && isDev) {
      console.log('[WebSocket] Dev mode: connecting without auth token')
    }

    // Reset messages and load history for existing sessions
    setMessages([])

    // Fetch historical messages from REST API before connecting WebSocket
    const loadHistory = async () => {
      try {
        const headers: Record<string, string> = {}
        if (token) headers['Authorization'] = `Bearer ${token}`
        const res = await fetchWithTimeout(
          `${API_BASE_URL}/api/chat/sessions/${sessionId}/messages`,
          { headers },
        )
        if (res.ok) {
          const data = await res.json()
          if (Array.isArray(data) && data.length > 0) {
            setMessages(data.map((m: Record<string, unknown>) => ({
              id: (m.id as string) ?? null,
              session_id: sessionId,
              role: (m.role as MessageRole) ?? MessageRole.AGENT,
              content: (m.content as string) ?? '',
              workflow_steps: (m.workflow_steps as WorkflowStep[]) ?? [],
              artifacts: (m.artifacts as Artifact[]) ?? [],
              created_at: (m.created_at as string) ?? new Date().toISOString(),
            })))
          }
        }
      } catch {
        // New session or API unavailable — start fresh
      }
    }
    loadHistory()

    shouldReconnectRef.current = true
    connect()

    return () => {
      shouldReconnectRef.current = false
      isIntentionalDisconnectRef.current = true // Mark as intentional for StrictMode
      if (reconnectTimeoutRef.current) {
        clearTimeout(reconnectTimeoutRef.current)
      }
      wsRef.current?.close()
    }
  // eslint-disable-next-line react-hooks/exhaustive-deps -- reconnect on session or token change
  }, [sessionId, token])

  return {
    messages,
    connectionStatus,
    reconnectAttempts,
    sendMessage,
    disconnect,
    reconnect,
  }
}
